<script setup lang="ts">
import { computed, ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import { ChevronDown } from "@lucide/vue";
import type { LocalModelType } from "@/features/BuildingProject";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import Popover from "@/components/Reusables/Popover/Popover.vue";
import TagItem from "@/components/Reusables/Utils/TagItem.vue";
import ModelChecklist from "../ModelChecklist/ModelChecklist.vue";
import CustomModels from "../WizardFlow/ConfigureSpeckleConnection/WithSpeckleConnection/CustomModels.vue";

/**
 * A project's models as one form field: the ticked models as removable chips in the field's box, and a panel
 * that overlays the form beside it holding the checklist with the create-model row and a Done button. The
 * start dialog is a short form, and a list of every suggested model plus an input made its one required
 * list the tallest thing on it.
 *
 * The panel is an overlay, not teleported and not in flow, and the caller says which way it opens. Both
 * other shapes were tried in the dialog: a teleported panel floated past the dialog's edge and read as a
 * second window, and an in-flow panel under the last field grew the dialog off the bottom of the screen.
 * Opening UPWARD over the fields above keeps it inside the dialog and inside the view. Presentational
 * like the checklist: the caller owns the list.
 */
const props = withDefaults(
  defineProps<{
    models: LocalModelType[];
    label?: string;
    required?: boolean;
    /** Which way the panel opens. "top-start" for a field at the bottom of its form. */
    placement?: "top-start" | "bottom-start";
    /** What the closed field says while nothing is ticked. */
    placeholder?: string;
    /** One line at the top of the panel. */
    caption?: string;
    /** What an empty list says. The caller knows why it is empty. */
    emptyText?: string;
  }>(),
  {
    label: "Models",
    required: false,
    placement: "bottom-start",
    placeholder: "No models yet",
    caption: "",
    emptyText: "No suggested models for this country yet. Create one below.",
  }
);

const emit = defineEmits<{
  toggleModel: [model: LocalModelType];
  setAll: [active: boolean];
  addModel: [model: LocalModelType];
}>();

const id = ref(uuidv4());
const activeModels = computed(() => props.models.filter((model) => model.isActive));
const existingModelNames = computed(() => props.models.map((model) => model.name));
</script>

<template>
  <div class="flex w-full flex-col">
    <!-- h-6 as in SelectInputNew, so this field's box lines up with the fields beside it. -->
    <div class="flex h-6 items-center">
      <InputLabel
        :id="id"
        :label="label"
        :required="required" />
    </div>
    <!-- Not teleported: the panel stays inside whatever holds the field. Popover owns outside-click, Escape
         (stopped before the modal's own handler sees it) and returning focus to the trigger. -->
    <Popover
      :placement="placement"
      panel-class="w-full p-px-8 flex flex-col gap-2"
      :aria-label="label">
      <template #trigger="{ toggle, isOpen }">
        <!-- The field's box. Each ticked model is a chip with its own Remove, so one click unticks it without
             opening the panel; the combobox button takes the rest of the row (and the whole row while nothing
             is ticked) and carries the label, the chevron and aria-required like the selects beside it. -->
        <div class="rounded-8 bg-slate-0 flex min-h-9 w-full flex-wrap items-center gap-1 border border-slate-300 p-1">
          <TagItem
            v-for="model in activeModels"
            :key="model.name"
            :label="model.name"
            color="neutral"
            can-be-deleted
            @on-delete="emit('toggleModel', model)" />
          <button
            :id="id"
            type="button"
            role="combobox"
            aria-haspopup="dialog"
            :aria-expanded="isOpen"
            :aria-required="required || undefined"
            class="focus-ring rounded-4 t-small relative flex h-7 min-w-32 flex-1 items-center pr-8 pl-2 text-left"
            @click="toggle">
            <span
              v-if="activeModels.length === 0"
              class="text-type-muted truncate">
              {{ placeholder }}
            </span>
            <ChevronDown
              class="icon-sm text-type-muted duration-fast ease-standard absolute right-2 transition-transform"
              :class="{ 'rotate-180': isOpen }"
              :stroke-width="1.5"
              aria-hidden="true" />
          </button>
        </div>
      </template>

      <template #default="{ close }">
        <!-- Capped so a country with many suggestions cannot push the panel past the top of the dialog. The
             horizontal padding and negative margin keep the rows' bleed inside the scrolling box. -->
        <div class="-mx-px-8 px-px-8 max-h-60 overflow-y-auto">
          <ModelChecklist
            :models="models"
            label=""
            :caption="caption"
            :empty-text="emptyText"
            @toggle-model="emit('toggleModel', $event)"
            @set-all="emit('setAll', $event)" />
        </div>
        <!-- Labelled: ties the row below to the checklist above as the other way to get a model on the
             list, rather than reading as an unrelated field that happens to follow a divider. -->
        <Divider
          label="or"
          spacing="sm" />
        <CustomModels
          compact
          :existing-model-names="existingModelNames"
          @add-new-model="emit('addModel', $event)" />
        <!-- Ticking a row changes nothing outside the panel, so Done is the visible way out; Escape works too. -->
        <div class="flex justify-end pt-1">
          <button
            type="button"
            class="focus-ring interactive-active duration-fast ease-standard rounded-8 t-small-bold bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover h-9 px-4 text-white transition-colors"
            @click="close">
            Done
          </button>
        </div>
      </template>
    </Popover>
  </div>
</template>
